// AnchorClientPanel — per-client ANCHOR surface inside the
// ClientDetailModal.
//
// Three sections:
//   1. ACA issues for this client (compact list with status pills
//      + inline solve/escalate)
//   2. Coverage checkboxes (writes client_coverages — drives the
//      cross-sell scanner's "missing" computation)
//   3. Cross-sell controls (per-client pause + per-coverage skip
//      toggles)
//
// Resolves the displayed CRM client → public.clients.id via phone
// match — same pattern as ClientWorkflowsPanel.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function normalizePhone(p) {
  return String(p || '').replace(/[^\d]/g, '').slice(-10);
}

const COVERAGE_DEFS = [
  { col: 'has_aca_health',         label: 'ACA Health',           emoji: '🩺', primary: true },
  { col: 'has_private_health',     label: 'Private health (PPO)', emoji: '🏥', primary: true },
  { col: 'has_medicare',           label: 'Medicare',             emoji: '🪪', primary: true },
  { col: 'has_dental',             label: 'Dental',               emoji: '🦷', crossSell: 'dental' },
  { col: 'has_vision',             label: 'Vision',               emoji: '👁️', crossSell: 'vision' },
  { col: 'has_life',               label: 'Life',                 emoji: '🛡️', crossSell: 'life' },
  { col: 'has_final_expense',      label: 'Final expense',        emoji: '🕯️', crossSell: 'final_expense' },
  { col: 'has_iul',                label: 'IUL',                  emoji: '📈' },
  { col: 'has_accident',           label: 'Accident',             emoji: '🩹', crossSell: 'accident' },
  { col: 'has_hospital_indemnity', label: 'Hospital indemnity',   emoji: '🏥', crossSell: 'hospital_indemnity' },
  { col: 'has_critical_illness',   label: 'Critical illness',     emoji: '⚕️', crossSell: 'critical_illness' },
  { col: 'has_cancer',             label: 'Cancer',               emoji: '🎗️', crossSell: 'cancer' },
  { col: 'has_disability',         label: 'Disability',           emoji: '♿', crossSell: 'disability' },
  { col: 'has_short_term',         label: 'Short-term',           emoji: '⏱️' },
  { col: 'has_telehealth',         label: 'Telehealth',           emoji: '💻' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }); } catch { return '—'; }
}

const ISSUE_TYPE_LABELS = {
  aor: 'AOR', plan_expiring: 'Plan expiring',
  docs_required: 'Docs', income_docs: 'Income docs',
  identity_docs: 'Identity docs', immigration_docs: 'Immigration docs',
  payment_due: 'Payment due', payment_failed: 'Payment failed',
  policy_inactive: 'Inactive', renewal_needed: 'Renewal',
  consent_needed: 'Consent', marketplace_notice: 'Notice',
  application_review: 'Review',
};

const STATUS_LABEL = {
  open: 'Open', not_solved: 'Working', waiting_client: 'Waiting client',
  waiting_carrier: 'Waiting carrier', needs_human: 'Needs you',
  solved: 'Solved', client_unresponsive: 'Unresponsive',
};

// ── Hook: resolve displayed client → clients row ─────────────
function useResolvedClient(clientHint, currentUser) {
  const [state, setState] = React.useState({ loading: true, clientId: null });
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!currentUser?.id) {
        if (alive) setState({ loading: false, clientId: null });
        return;
      }
      const phone10 = normalizePhone(clientHint?.phone);
      if (phone10) {
        const { data } = await supabase
          .from('clients').select('id').eq('owner_id', currentUser.id)
          .ilike('phone', `%${phone10}%`).limit(1).maybeSingle();
        if (alive && data?.id) { setState({ loading: false, clientId: data.id }); return; }
      }
      const name = String(clientHint?.name || '').trim();
      if (name) {
        const { data } = await supabase
          .from('clients').select('id').eq('owner_id', currentUser.id)
          .ilike('name', name).limit(1).maybeSingle();
        if (alive && data?.id) { setState({ loading: false, clientId: data.id }); return; }
      }
      if (alive) setState({ loading: false, clientId: null });
    })();
    return () => { alive = false; };
  }, [clientHint?.phone, clientHint?.name, currentUser?.id]);
  return state;
}

// ── Sub-component: CoveragesGrid ─────────────────────────────
function CoveragesGrid({ clientId, currentUser, coverages, setCoverages, showToast }) {
  const [busy, setBusy] = React.useState(false);

  async function toggle(col) {
    if (busy) return;
    const newVal = !coverages?.[col];
    const next = { ...(coverages || {}), [col]: newVal };
    setCoverages(next);
    setBusy(true);
    try {
      if (coverages?.client_id) {
        await supabase.from('client_coverages').update({ [col]: newVal }).eq('client_id', clientId);
      } else {
        await supabase.from('client_coverages').insert({
          client_id: clientId,
          owner_id:  currentUser.id,
          [col]:     newVal,
        });
      }
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
      setCoverages(coverages);  // rollback
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5">
      {COVERAGE_DEFS.map(c => {
        const on = Boolean(coverages?.[c.col]);
        return (
          <button
            key={c.col}
            onClick={() => toggle(c.col)}
            disabled={busy}
            className={`px-2 py-1.5 rounded border text-left transition-colors ${
              on
                ? 'bg-emerald-50 border-emerald-400 text-emerald-900'
                : c.crossSell
                  ? 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-base">{c.emoji}</span>
              <span className="text-xs font-medium">{c.label}</span>
              {on && <span className="ml-auto text-[10px] font-bold">✓</span>}
            </div>
            {!on && c.crossSell && (
              <div className="text-[10px] text-amber-700 mt-0.5">Cross-sell opportunity</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Sub-component: NurtureControls ───────────────────────────
function NurtureControls({ clientId, currentUser, showToast }) {
  const [paused, setPaused] = React.useState(false);
  const [pauseReason, setPauseReason] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from('nurture_settings_client')
        .select('cross_sell_paused, pause_reason')
        .eq('client_id', clientId).maybeSingle();
      if (alive) {
        setPaused(Boolean(data?.cross_sell_paused));
        setPauseReason(data?.pause_reason || '');
        setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [clientId]);

  async function togglePause() {
    const next = !paused;
    let reason = pauseReason;
    if (next && !reason) {
      reason = window.prompt('Reason for pausing cross-sell (optional):') || '';
    }
    setPaused(next); setPauseReason(reason);
    try {
      const { data: existing } = await supabase
        .from('nurture_settings_client').select('client_id').eq('client_id', clientId).maybeSingle();
      if (existing) {
        await supabase.from('nurture_settings_client').update({
          cross_sell_paused: next,
          pause_reason:      reason || null,
        }).eq('client_id', clientId);
      } else {
        await supabase.from('nurture_settings_client').insert({
          client_id: clientId, owner_id: currentUser.id,
          cross_sell_paused: next,
          pause_reason:      reason || null,
        });
      }
      showToast(next ? 'Cross-sell paused for this client.' : 'Cross-sell resumed.');
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
      setPaused(!next);
    }
  }

  if (!loaded) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap">
      <div>
        <div className="text-sm font-semibold text-slate-900">Cross-sell nurture</div>
        <div className="text-xs text-slate-500">
          {paused ? 'Paused — no cross-sell messages for this client.' : 'Active — ANCHOR may pitch missing coverages.'}
          {paused && pauseReason && <span className="italic"> ({pauseReason})</span>}
        </div>
      </div>
      <button
        onClick={togglePause}
        className={`text-xs px-3 py-1.5 rounded font-semibold ${
          paused
            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
            : 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
        }`}
      >
        {paused ? 'Resume' : 'Pause cross-sell'}
      </button>
    </div>
  );
}

// ── Main exported panel ──────────────────────────────────────
export default function AnchorClientPanel({ clientHint, currentUser, showToast = () => {} }) {
  const { loading: resolving, clientId } = useResolvedClient(clientHint, currentUser);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [coverages, setCoverages] = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!clientId) return;
    setError(null);
    try {
      const body = await apiFetch(`/api/anchor/client-issues?client_id=${encodeURIComponent(clientId)}`);
      setData(body);
      setCoverages(body.coverages);
    } catch (e) {
      setError(e.message);
    }
  }, [clientId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  async function handleAction(action, issueId) {
    setBusy(true);
    try {
      await apiFetch('/api/anchor/issue-action', {
        method: 'POST',
        body: JSON.stringify({ issue_id: issueId, action }),
      });
      showToast(`Issue ${action.replace('_',' ')}d.`);
      await refresh();
    } catch (e) {
      showToast(`Action failed: ${e.message}`, 'error');
    } finally { setBusy(false); }
  }

  if (resolving) {
    return <div className="border-t border-slate-200 bg-white p-4 text-xs text-slate-500">Looking up CRM client record…</div>;
  }
  if (!clientId) {
    return (
      <div className="border-t border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">⚓ ANCHOR</h3>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          No matching Clients record. Use the Workflow panel above to create one — ANCHOR + cross-sell hook in once it exists.
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 bg-white p-4 space-y-4">
      <div>
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">⚓ ANCHOR · ACA retention + cross-sell</h3>
        <p className="text-xs text-slate-500">Open issues, coverage profile, and per-client nurture controls.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800">
          {error}
          {/relation.*aca_issues.*does not exist/i.test(error) && (
            <div className="mt-1">Run migration 101 in Supabase to enable ANCHOR.</div>
          )}
        </div>
      )}

      {data && (
        <>
          {/* Issues section */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Open issues ({data.issues.filter(i => !['solved','client_unresponsive'].includes(i.status)).length})</h4>
            {data.issues.length === 0 ? (
              <div className="text-xs text-slate-500 italic">No issues tracked for this client.</div>
            ) : (
              <div className="space-y-1.5">
                {data.issues.slice(0, 8).map(i => (
                  <div key={i.id} className="bg-slate-50 border border-slate-200 rounded p-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-700">{ISSUE_TYPE_LABELS[i.issue_type] || i.issue_type}</span>
                    <span className="text-xs text-slate-500">· {STATUS_LABEL[i.status] || i.status}</span>
                    {i.deadline && <span className="text-xs text-slate-500">· deadline {fmtDate(i.deadline)}</span>}
                    {!['solved','client_unresponsive'].includes(i.status) && (
                      <div className="ml-auto flex items-center gap-1">
                        <button disabled={busy} onClick={() => handleAction('solve', i.id)} className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">✓ Solve</button>
                        <button disabled={busy} onClick={() => handleAction('escalate', i.id)} className="text-[11px] px-2 py-0.5 rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">Escalate</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coverages section */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">
              Coverages this client has · <span className="text-xs font-normal text-slate-500">amber = missing + ANCHOR can pitch</span>
            </h4>
            <CoveragesGrid
              clientId={clientId}
              currentUser={currentUser}
              coverages={coverages}
              setCoverages={setCoverages}
              showToast={showToast}
            />
          </div>

          {/* Nurture controls */}
          <NurtureControls clientId={clientId} currentUser={currentUser} showToast={showToast} />
        </>
      )}
    </div>
  );
}
